import { useEffect, useState } from "react";
import { type TLUiComponents, Tldraw, useEditor } from "tldraw";
import "tldraw/tldraw.css";

export function CanvasStage({
	questionImageUrl,
	showQuestion,
	onDone,
}: {
	questionImageUrl: string;
	showQuestion: boolean;
	onDone: (blob: Blob) => void | Promise<void>;
}) {
	const [mounted, setMounted] = useState(false);
	useEffect(() => setMounted(true), []);

	return (
		<div className="absolute inset-0 flex overflow-hidden">
			{showQuestion && (
				<aside className="w-80 lg:w-96 shrink-0 border-r bg-zinc-50 flex flex-col overflow-hidden">
					<div className="flex-1 overflow-auto p-3 flex items-start justify-center">
						<img
							src={questionImageUrl}
							alt="Math question"
							className="max-w-full object-contain"
						/>
					</div>
				</aside>
			)}
			<div className="relative flex-1 min-w-0 bg-zinc-100">
				{mounted && (
					<Tldraw
						components={
							{
								SharePanel: () => <DoneButton onDone={onDone} />,
							} satisfies TLUiComponents
						}
					/>
				)}
			</div>
		</div>
	);
}

function DoneButton({
	onDone,
}: {
	onDone: (blob: Blob) => void | Promise<void>;
}) {
	const editor = useEditor();
	const [busy, setBusy] = useState(false);

	async function handleClick() {
		if (busy) return;
		const ids = editor.getCurrentPageShapeIds();
		if (ids.size === 0) {
			alert("Draw your working out before clicking Done.");
			return;
		}
		setBusy(true);
		try {
			const { blob } = await editor.toImage([...ids], {
				format: "png",
				background: true,
				scale: 2,
				pixelRatio: 2,
				padding: 32,
			});
			await onDone(blob);
		} finally {
			setBusy(false);
		}
	}

	return (
		<button
			type="button"
			onClick={handleClick}
			disabled={busy}
			style={{ pointerEvents: "all" }}
			className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium shadow hover:bg-blue-700 disabled:opacity-60"
		>
			{busy ? "Recognizing…" : "Done"}
		</button>
	);
}
